import { getQueryParamValue } from "@chatfood/core-utils";
import { useToast } from "@chatfood/design-system";
import {
  ref,
  computed,
  readonly,
  useBasketStore,
  useDirectBackend,
  useOutletStore,
  useBugReporter,
  useMenusStore,
} from "#imports";
import { useNavigation } from "~/composables/use-navigation";
import { useLocale } from "~/composables/use-locale";

export const usePluLoyalty = () => {
  const { toastError, toastWarning, toastSuccess } = useToast();
  const { t } = useLocale();
  const { menuRepo } = useDirectBackend();
  const { route, goToItem } = useNavigation();
  const { report } = useBugReporter();

  const outlet = useOutletStore();
  const basket = useBasketStore();
  const menus = useMenusStore();

  const postItemAddedLoyaltyId = ref<string>();
  const itemPluId = ref<string>();
  const isApplyingLoyalty = ref(false);

  const handlePluLoyalty = async () => {
    const itemPlu = getQueryParamValue(route.value.query, "item-plu");
    const loyaltyId = getQueryParamValue(route.value.query, "with-loyalty");

    if (!itemPlu) return;

    try {
      const res = await menuRepo.getItems({
        outletId: outlet.id,
        plus: [itemPlu],
        menuIds: menus.menuIds,
      });

      const item = res.data[0];

      if (!item) {
        toastError(t("pages.outlet.plu_loyalty.invalid_plu_error"));
        return;
      }

      postItemAddedLoyaltyId.value = loyaltyId;
      itemPluId.value = item.id;
      goToItem(item.id);
    } catch (e) {
      report(e);
    }
  };

  const clearItemPluFlow = () => {
    postItemAddedLoyaltyId.value = undefined;
    itemPluId.value = undefined;
  };

  const onItemHidden = async () => {
    if (!postItemAddedLoyaltyId.value || !itemPluId.value) {
      return clearItemPluFlow();
    }

    const isItemAdded = basket.items.some(
      (item) => item.itemId === itemPluId.value,
    );

    if (!isItemAdded) {
      return clearItemPluFlow();
    }

    const appliedLoyaltyId: string | undefined = basket.loyalties?.[0]?.id;

    if (appliedLoyaltyId) {
      if (appliedLoyaltyId === postItemAddedLoyaltyId.value) {
        return clearItemPluFlow();
      }

      if (appliedLoyaltyId !== postItemAddedLoyaltyId.value) {
        toastWarning(t("pages.outlet.plu_loyalty.existing_loyalty_warning"));
        return clearItemPluFlow();
      }
    }

    try {
      isApplyingLoyalty.value = true;

      if (basket.discountCode) {
        await basket.removeDiscount();
      }

      await basket.applyLoyalty(postItemAddedLoyaltyId.value);

      if (
        basket.loyalties.some(
          (loyalty) => loyalty.id === postItemAddedLoyaltyId.value,
        )
      ) {
        toastSuccess(t("pages.outlet.plu_loyalty.loyalty_success"));
      } else {
        toastError(t("pages.outlet.plu_loyalty.loyalty_eligibility_error"));
      }
    } catch {
      toastError(t("pages.outlet.plu_loyalty.loyalty_application_error"));
    } finally {
      isApplyingLoyalty.value = false;
      clearItemPluFlow();
    }
  };

  const hasPendingLoyalty = computed(() =>
    Boolean(postItemAddedLoyaltyId.value && itemPluId.value),
  );

  return {
    isApplyingLoyalty: readonly(isApplyingLoyalty),
    hasPendingLoyalty,
    handlePluLoyalty,
    onItemHidden,
    clearItemPluFlow,
  };
};
